/**
 * Settings › Customers › Proposal — `docs/proposal.md` §10.4.
 *
 * Three places decide how a proposal reads, broad to narrow, and each starts
 * as "the same as the one above": **this page** is what every new quote is
 * created with, the quote's own Proposal tab changes that quote, and one
 * visit's *On the proposal* popover changes that visit. The copy says so in
 * as many words, because a company that changes something here and sees no
 * change on last week's quote will otherwise file it as a bug — the copy at
 * creation is the whole point (§10): changing a default must never alter a
 * document somebody has already sent.
 *
 * **Card 1 is `New proposals start like this`**, and the two fields with no
 * home anywhere else live on it: the sales-tax rate and what the proposal
 * calls it. Until this page existed the rate was a literal in the server
 * (`taxRate = 0.06`, right by accident for one company), and migration `0113`
 * made it a nullable column whose **NULL means no tax line at all** — a blank
 * is a question somebody asks and a wrong figure is one nobody does. So the
 * field is empty rather than zero, and the help text says what empty does.
 *
 * **Card 2 is the phrase library**: the lines a proposal says under a visit.
 * Exactly one is the default, which is what a visit says when it picks none,
 * and its usage count is the visits with nothing of their own — so the
 * numbers add up to the quotes a company has rather than to something larger.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULT_TIMING_PHRASE, PROPOSAL_KINDS,
  type ProposalDefaultTerms, type ProposalDefaults, type ProposalKind, type ProposalPhraseView,
  type ProposalVisit, type MeResponse,
} from '@rowted/shared'
import { can } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { useSettingsBar } from '../settings/settings-bar.js'
import { Band, Button, EmptyState, Segmented, Switch, Tag } from '../components/primitives.js'
import { SettingsCard } from '../components/settings-card.js'
import { TextInput } from '../components/form/index.js'
import { Popover } from '../components/overlays.js'
import { IconCheck, IconFile, IconGrip, IconMore, IconPlus, IconWarn } from '../frame/icons.js'
import { TimingPreview } from '../quote/proposal/visit-card.js'
import { brandSkin } from '../quote/proposal/brand.js'
import { ApprovalSettingCard } from '../job-stages/approval-setting.js'

interface Payload {
  defaults: ProposalDefaults
  phrases: ProposalPhraseView[]
  /** Only on the GET: the PUT answers with the two halves it changes. */
  terms?: ProposalDefaultTerms | null
  brandColor?: string | null
}

export function ProposalSettingsPage({ me }: { me: MeResponse }) {
  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const [draft, setDraft] = useState<ProposalDefaults | null>(null)
  const [saved, setSaved] = useState<ProposalDefaults | null>(null)
  const [phrases, setPhrases] = useState<ProposalPhraseView[]>([])
  const [terms, setTerms] = useState<ProposalDefaultTerms | null>(null)
  const [brandColor, setBrandColor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<{ message: string; field?: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const body = await request<Payload>('/settings/proposal')
      setDraft(body.defaults)
      setSaved(body.defaults)
      setPhrases(body.phrases)
      setTerms(body.terms ?? null)
      setBrandColor(body.brandColor ?? null)
    } catch (cause) {
      setError({ message: cause instanceof Error ? cause.message : 'That could not be loaded.' })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const dirty = draft !== null && saved !== null
    && JSON.stringify(draft) !== JSON.stringify(saved)

  const save = useCallback(async () => {
    if (!draft) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const body = await request<Payload>('/settings/proposal',
        { method: 'PUT', body: draft })
      setDraft(body.defaults)
      setSaved(body.defaults)
      setNotice('Saved. New quotes start like this from now on.')
    } catch (cause) {
      const problem = cause as { message?: string; field?: string }
      setError({ message: problem.message ?? 'That did not save.', field: problem.field })
    } finally {
      setBusy(false)
    }
  }, [draft])

  const barActions = useSettingsBar({
    title: 'Proposal',
    sub: 'what customers read on a proposal',
    actions: canEdit
      ? (
        <Button
          variant="primary"
          className="h-8 text-[12.5px]"
          disabled={!dirty || busy}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      )
      : undefined,
  })

  if (!draft) {
    return (
      <div className="p-5">
        {barActions}
        {error
          ? <EmptyState title="Not available" body={error.message} />
          : <p className="m-0 text-body text-text-muted">Loading…</p>}
      </div>
    )
  }

  const set = <K extends keyof ProposalDefaults>(key: K, value: ProposalDefaults[K]) =>
    setDraft({ ...draft, [key]: value })

  /*
   * A price on every line only makes sense when every kind of line is listed:
   * prices on three of four kinds is a column of figures that does not add up
   * to the total beside it. The server refuses the pair, so the switch is
   * disabled rather than left to fail the save.
   */
  const allKinds = PROPOSAL_KINDS.every((kind) => draft.kinds.includes(kind))

  return (
    <div className="flex items-start gap-5 p-5 max-xl:flex-col">
      {barActions}

      <div className="flex min-w-0 max-w-[868px] flex-1 flex-col gap-4 max-xl:max-w-full">
        {notice && <Band tone="green" icon={<IconCheck size={15} />}>{notice}</Band>}
        {error && (
          <Band tone="amber" icon={<IconWarn size={15} />}>
            {error.field ? `${error.field}: ` : ''}{error.message}
          </Band>
        )}

        <SettingsCard
          title="New proposals start like this"
          sub="each quote and each visit can change these"
        >
          <Row
            label="Detail"
            help="How far a visit opens. Choices always show."
          >
            <Segmented
              label="Detail"
              size="field"
              value={draft.detail}
              onChange={(next) => set('detail', next)}
              options={[
                { value: 'visits', label: 'Visits' },
                { value: 'services', label: 'Services' },
                { value: 'lines', label: 'Everything' },
              ]}
            />
          </Row>

          <Row
            label="Lines to list"
            help="Fees are off because to a customer they read as an upcharge. They are
                  still in the price."
          >
            <div className="flex flex-wrap gap-1.5">
              {PROPOSAL_KINDS.map((kind) => {
                const on = draft.kinds.includes(kind)
                return (
                  <button
                    key={kind}
                    type="button"
                    disabled={!canEdit}
                    aria-pressed={on}
                    onClick={() => set('kinds', (on
                      ? draft.kinds.filter((one) => one !== kind)
                      : [...draft.kinds, kind]) as ProposalKind[])}
                    className={cn('inline-flex h-[30px] items-center gap-[5px] rounded-chip border',
                      'px-3 text-[12.5px] font-semibold',
                      on
                        ? 'border-accent-line bg-accent-soft text-accent-dark'
                        : 'border-dashed border-line-strong bg-surface text-text-faint')}
                  >
                    {on && <IconCheck size={12} />}
                    {KIND_WORDS[kind]}
                  </button>
                )
              })}
            </div>
          </Row>

          <Row
            label="Show a price on every line"
            help={allKinds
              ? 'Each line carries its own figure.'
              : 'Available once all four kinds are listed — prices on some of the lines '
                + 'is a column that does not add up to the total beside it.'}
          >
            <Switch
              checked={draft.linePrices}
              disabled={!canEdit || !allKinds}
              onChange={(next) => set('linePrices', next)}
              label="Show a price on every line"
              /* The Row above already says it — a second copy beside the
                 track reads as a stutter, and `hideLabel` keeps it in the
                 accessible name where it is still needed. */
              hideLabel
            />
          </Row>

          <Row
            label="Crew hours"
            help="Labor says how many hours. Off keeps the line and drops the number."
          >
            <Switch
              checked={draft.showHours}
              disabled={!canEdit}
              onChange={(next) => set('showHours', next)}
              label="Show crew hours"
              hideLabel
            />
          </Row>

          {/*
           * Aaron, step 26: the same switch as the Proposal tab's, as the
           * default every new quote starts with (0130). Not on board 15,
           * which predates it; recorded in docs/as-drawn/step-26.md.
           */}
          <Row
            label="Material and fee quantities"
            help="Materials and fees say how much. Off keeps the line and drops the number."
          >
            <Switch
              checked={draft.showQuantities}
              disabled={!canEdit}
              onChange={(next) => set('showQuantities', next)}
              label="Show material and fee quantities"
              hideLabel
            />
          </Row>

          <Row
            label="Price shown as"
            help="Follows how the visits bill: monthly, per visit, deposit and balance."
          >
            <Segmented
              label="Price shown as"
              size="field"
              value={draft.priceView}
              onChange={(next) => set('priceView', next)}
              options={[
                { value: 'plan', label: 'Billing plan' },
                { value: 'visit', label: 'Per visit' },
                { value: 'both', label: 'Both' },
              ]}
            />
          </Row>

          <Row
            label="When the visits happen"
            help="A count, a frequency or a season in months. Never a weekday or a date by
                  default."
          >
            <Segmented
              label="When the visits happen"
              size="field"
              value={draft.timing}
              onChange={(next) => set('timing', next)}
              options={[
                { value: 'count', label: 'Count' },
                { value: 'frequency', label: 'Weekly' },
                { value: 'season', label: 'Season' },
                /*
                 * Dates is offered here whatever the switch below says,
                 * because this *is* the page that holds the switch: hiding
                 * the segment the company is deciding about would be a
                 * control that cannot be set to the thing it controls.
                 */
                { value: 'dates', label: 'Dates' },
              ]}
            />
          </Row>

          <Row
            label="Estimators can show dates"
            help="Adds Dates to that choice on a visit, for the rare quote where dates are
                  already held. Nothing shows dates unless someone picks it."
          >
            <Switch
              checked={draft.datesAllowed}
              disabled={!canEdit}
              onChange={(next) => set('datesAllowed', next)}
              label="Estimators can show dates"
              hideLabel
            />
          </Row>

          <Row label="Good for" help="How long a proposal is valid, counted from the day it is sent.">
            <span className="flex items-center gap-2">
              <span className="w-[72px]">
                <TextInput
                  value={String(draft.validDays)}
                  disabled={!canEdit}
                  inputMode="numeric"
                  onChange={(event) => {
                    const typed = Number.parseInt(event.target.value, 10)
                    set('validDays', Number.isFinite(typed) ? typed : 0)
                  }}
                />
              </span>
              <span className="text-[13px] text-text-body">days</span>
            </span>
          </Row>

          <Row
            label="Sales tax estimate"
            help="Left blank, the proposal shows no tax line at all — which is the honest
                  answer when nobody has said what the rate is. A figure that is wrong on a
                  document somebody signs is worse than no figure."
          >
            <span className="flex items-center gap-2">
              <span className="w-[86px]">
                <TextInput
                  value={draft.taxRatePct === null ? '' : String(draft.taxRatePct)}
                  disabled={!canEdit}
                  placeholder="none"
                  inputMode="decimal"
                  onChange={(event) => {
                    const text = event.target.value.trim()
                    if (text === '') { set('taxRatePct', null); return }
                    const typed = Number.parseFloat(text)
                    set('taxRatePct', Number.isFinite(typed) ? typed : 0)
                  }}
                />
              </span>
              <span className="text-[13px] text-text-body">%</span>
            </span>
          </Row>

          <Row
            label="What the proposal calls it"
            help="Printed beside the estimate — “PA 6% on materials”, “Sales tax”."
          >
            <TextInput
              value={draft.taxLabel ?? ''}
              disabled={!canEdit || draft.taxRatePct === null}
              placeholder={draft.taxRatePct === null ? 'no rate, no line' : 'Sales tax'}
              maxLength={80}
              onChange={(event) => set('taxLabel', event.target.value || null)}
            />
          </Row>
        </SettingsCard>

        <PhraseLibrary
          phrases={phrases}
          canEdit={canEdit}
          onChanged={setPhrases}
          onError={(message) => setError({ message })}
        />

        <TermsCard terms={terms} />

        <NextSteps draft={draft} canEdit={canEdit} onChange={(next) => set('nextSteps', next)} />

        {/* Board 3 C (`docs/job-stages.md` §8 C): what the office's Approve must record. */}
        <ApprovalSettingCard
          value={{
            how: draft.approvalRequireHow ?? false,
            note: draft.approvalRequireNote ?? false,
            proof: draft.approvalRequireProof ?? false,
          }}
          disabled={!canEdit}
          onChange={(next) => setDraft({
            ...draft,
            approvalRequireHow: next.how,
            approvalRequireNote: next.note,
            approvalRequireProof: next.proof,
          })}
        />
      </div>

      <aside className="flex w-[364px] shrink-0 flex-col gap-3.5 max-xl:w-full">
        <ReadsNow draft={draft} phrases={phrases} brandColor={brandColor} />
        <SettingsCard title="Who can change what">
          <div className="flex flex-col gap-2.5 px-[18px] py-3.5 text-[12.5px] leading-[1.45]
                          text-text">
            <Hand number="1" title="This page."
              body="What every new proposal starts with. Office managers and owners." />
            <Hand number="2" title="The quote."
              body="Its Proposal tab changes the defaults for that quote only." />
            <Hand number="3" title="One visit."
              body="On the proposal on the visit overrides the quote for that visit." />
          </div>
        </SettingsCard>
      </aside>
    </div>
  )
}

// ---------------------------------------------------------------------------
// How a visit reads now
// ---------------------------------------------------------------------------

/**
 * Two sample visits drawn by the page's own timing band (board 15), live
 * against the draft: change "When the visits happen" or "Price shown as" and
 * the band beside it changes before anybody saves. The visits are invented —
 * weekly lawn care billed by the month and a one-off cleanup — because the
 * point is the company's *wording*, and the two shapes between them show
 * every part of it: a count, a season, a phrase and a price said two ways.
 *
 * Dates has nothing to draw without real dates, so it previews as the server
 * renders it with none: the season.
 */
function ReadsNow({ draft, phrases, brandColor }: {
  draft: ProposalDefaults
  phrases: ProposalPhraseView[]
  brandColor: string | null
}) {
  const phrase = phrases.find((one) => one.isDefault)?.text ?? DEFAULT_TIMING_PHRASE
  const mode = draft.timing === 'dates' ? 'season' : draft.timing
  const skin = brandSkin({
    name: '', logoUrl: null, markUrl: null, brandColor, band: true, tagline: null,
    addressLines: [], phone: null, email: null, licence: null, footer: null,
  })
  const lawn: ProposalVisit = sampleVisit({
    count: 9,
    mode,
    phrase,
    frequency: 'Weekly',
    season: { months: ['Oct', 'Nov', 'Dec'], label: 'October to early December' },
    price: {
      view: draft.priceView,
      plan: { kind: 'period', figure: 288, unit: 'month', every: 1, periods: 3,
        estimate: false, cap: null, text: 'for 3 months' },
      perVisit: 96, occurrences: 9, total: 864,
    },
  })
  const cleanup: ProposalVisit = sampleVisit({
    count: 1,
    mode: mode === 'season' || mode === 'frequency' ? 'count' : mode,
    phrase,
    frequency: null,
    season: null,
    price: {
      view: draft.priceView,
      plan: { kind: 'deposit_balance', figure: 396, unit: null, every: 1, periods: null,
        estimate: false, cap: null, text: '' },
      perVisit: 396, occurrences: 1, total: 396,
    },
  })

  return (
    <SettingsCard title="How a visit reads now">
      <div style={skin.vars} className="flex flex-col gap-1 px-4 pb-4 pt-3">
        <span className="text-[11.5px] text-text-muted">Weekly lawn care · billed monthly</span>
        <TimingPreview visit={lawn} />
        <span className="pt-3.5 text-[11.5px] text-text-muted">
          Fall bed cleanup · one visit, deposit and balance
        </span>
        <TimingPreview visit={cleanup} />
      </div>
    </SettingsCard>
  )
}

function sampleVisit({ count, mode, phrase, frequency, season, price }: {
  count: number
  mode: ProposalVisit['timing']['mode']
  phrase: string
  frequency: string | null
  season: ProposalVisit['timing']['season']
  price: ProposalVisit['price']
}): ProposalVisit {
  return {
    id: `sample-${count}`, name: '', description: '', kind: 'included', groupId: null,
    optionIds: [], recommended: false,
    count: { n: count, unit: count === 1 ? 'visit' : 'visits', openEnded: false },
    timing: {
      mode, phrase, facts: count === 1 ? 'One visit' : `${count} visits`,
      frequency, season, dates: [], project: null, rule: null,
    },
    price, detail: 'visits', kinds: [], showHours: false, recurring: null, oneTime: null,
    services: [],
  }
}

// ---------------------------------------------------------------------------
// The phrase library
// ---------------------------------------------------------------------------

function PhraseLibrary({ phrases, canEdit, onChanged, onError }: {
  phrases: ProposalPhraseView[]
  canEdit: boolean
  onChanged: (phrases: ProposalPhraseView[]) => void
  onError: (message: string) => void
}) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  const call = async (
    path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown,
  ) => {
    try {
      const answer = await request<{ phrases: ProposalPhraseView[] }>(
        `/settings/proposal${path}`, { method, body })
      onChanged(answer.phrases)
      return true
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'That did not save.')
      return false
    }
  }

  /*
   * Reordering sets the picker's order (board 15). The list moves at once and
   * each phrase whose place changed is written with its new position; the last
   * answer is the server's whole list, which is what the page keeps.
   */
  const move = async (id: string, target: string) => {
    const from = phrases.findIndex((one) => one.id === id)
    const to = phrases.findIndex((one) => one.id === target)
    if (from < 0 || to < 0 || from === to) return
    /*
     * Dropped on a row it lands where that row was: dragged down, after it,
     * dragged up, before it — otherwise nothing could ever be moved to last.
     */
    const order = phrases.filter((one) => one.id !== id)
    order.splice(to, 0, phrases[from]!)
    onChanged(order.map((one, index) => ({ ...one, sortOrder: index })))
    for (const [index, one] of order.entries()) {
      if (one.sortOrder === index) continue
      if (!await call(`/phrases/${one.id}`, 'PATCH', { sortOrder: index })) return
    }
  }

  return (
    <SettingsCard
      title="Wording"
      sub="the lines the proposal says under a visit. Pick one on any visit or write your own there"
    >
      {phrases.map((phrase) => (
        editing === phrase.id
          ? (
            <PhraseForm
              key={phrase.id}
              initial={phrase}
              onCancel={() => setEditing(null)}
              onSave={async (name, text) => {
                if (await call(`/phrases/${phrase.id}`, 'PATCH', { name, text })) setEditing(null)
              }}
            />
          )
          : (
            <PhraseRow
              key={phrase.id}
              phrase={phrase}
              canEdit={canEdit}
              dragging={dragging === phrase.id}
              over={over === phrase.id && dragging !== phrase.id}
              onDragStart={() => setDragging(phrase.id)}
              onDragEnd={() => { setDragging(null); setOver(null) }}
              onDragOver={() => setOver(phrase.id)}
              onDrop={() => {
                const id = dragging
                setDragging(null)
                setOver(null)
                if (id) void move(id, phrase.id)
              }}
              onEdit={() => setEditing(phrase.id)}
              onDefault={() => void call(`/phrases/${phrase.id}`, 'PATCH', { isDefault: true })}
              onRemove={() => void call(`/phrases/${phrase.id}`, 'DELETE')}
            />
          )
      ))}

      {phrases.length === 0 && (
        <div className="border-b border-line-faint px-[18px] py-3 text-[12.5px] text-text-muted">
          {`No phrases yet, so every visit says “${DEFAULT_TIMING_PHRASE}”.`}
        </div>
      )}

      {adding
        ? (
          <PhraseForm
            onCancel={() => setAdding(false)}
            onSave={async (name, text) => {
              if (await call('/phrases', 'POST', { name, text })) setAdding(false)
            }}
          />
        )
        : (
          <div className="flex flex-wrap items-center gap-2.5 px-[18px] py-3">
            {canEdit && (
              <Button className="h-[30px] text-[12.5px]" onClick={() => setAdding(true)}>
                <IconPlus size={15} /> Add phrase
              </Button>
            )}
            <span className="text-[12px] text-text-muted">
              Plain text, up to 160 characters. Reordering sets the order of the picker.
            </span>
          </div>
        )}
    </SettingsCard>
  )
}

/**
 * One phrase (board 15): a grip, the name in a 210px column with the Default
 * tag, what the customer reads, how many visits say it, and a ⋯ menu. The
 * verbs are in the menu rather than on the row because a row of five phrases
 * each carrying "Make default" and a bin is ten buttons around five sentences.
 */
function PhraseRow({ phrase, canEdit, dragging, over, onDragStart, onDragEnd, onDragOver, onDrop,
  onEdit, onDefault, onRemove }: {
  phrase: ProposalPhraseView
  canEdit: boolean
  dragging: boolean
  over: boolean
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: () => void
  onDrop: () => void
  onEdit: () => void
  onDefault: () => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement | null>(null)
  return (
    <div
      draggable={canEdit}
      onDragStart={(event) => { event.dataTransfer.setData('text/plain', phrase.id); onDragStart() }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => { event.preventDefault(); onDragOver() }}
      onDrop={(event) => { event.preventDefault(); onDrop() }}
      className={cn('flex items-center gap-3.5 border-b border-line-faint px-[18px] py-[11px]',
        'max-[900px]:flex-wrap',
        dragging && 'opacity-50', over && 'shadow-[inset_0_2px_0_rgb(var(--color-accent))]')}
    >
      {canEdit && (
        <span className="inline-flex shrink-0 cursor-grab text-line-strong" aria-hidden>
          <IconGrip size={14} />
        </span>
      )}
      <div className="flex w-[210px] shrink-0 items-center gap-2">
        <b className="truncate text-[13px] font-bold text-text">{phrase.name}</b>
        {phrase.isDefault && <Tag tone="ok">Default</Tag>}
      </div>
      <span className="min-w-0 flex-1 text-[13px] leading-[1.4] text-text-body">{phrase.text}</span>
      <span className="tnum w-[70px] shrink-0 text-right text-[12px] text-text-faint">
        {phrase.usedBy === 1 ? '1 visit' : `${phrase.usedBy} visits`}
      </span>
      {canEdit && (
        <>
          <button
            ref={button}
            type="button"
            aria-label={`More for ${phrase.name}`}
            onClick={() => setOpen((was) => !was)}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-button
                       text-text-faint hover:bg-hover hover:text-text"
          >
            <IconMore size={15} />
          </button>
          <Popover open={open} onClose={() => setOpen(false)} anchor={button.current}
            align="right" width={180} label={`More for ${phrase.name}`}>
            <div className="flex flex-col py-1">
              <PhraseMenuItem label="Edit" onClick={() => { setOpen(false); onEdit() }} />
              {!phrase.isDefault && (
                <PhraseMenuItem label="Make default"
                  onClick={() => { setOpen(false); onDefault() }} />
              )}
              {/*
                * Removing the default is refused by the server rather than
                * leaving the company with none — "exactly one default" is
                * the rule, and a control that breaks it is not offered.
                */}
              {!phrase.isDefault && (
                <PhraseMenuItem label="Remove" danger
                  onClick={() => { setOpen(false); onRemove() }} />
              )}
            </div>
          </Popover>
        </>
      )}
    </div>
  )
}

function PhraseMenuItem({ label, onClick, danger = false }: {
  label: string; onClick: () => void; danger?: boolean
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn('flex h-9 items-center px-3 text-left text-cell hover:bg-hover',
        danger ? 'text-danger' : 'text-text')}>
      {label}
    </button>
  )
}

/** Name and words, for a new phrase or one being edited in its row. */
function PhraseForm({ initial, onCancel, onSave }: {
  initial?: ProposalPhraseView
  onCancel: () => void
  onSave: (name: string, text: string) => Promise<void>
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [text, setText] = useState(initial?.text ?? '')
  return (
    <div className="flex flex-col gap-2 border-b border-line-faint bg-surface-alt px-[18px] py-3">
      <TextInput
        value={name}
        placeholder="A short name — “Weekly in season”"
        maxLength={60}
        onChange={(event) => setName(event.target.value)}
      />
      <TextInput
        value={text}
        placeholder="What the customer reads — “We’ll contact you with your schedule once you accept.”"
        maxLength={160}
        onChange={(event) => setText(event.target.value)}
      />
      <span className="flex items-center gap-2 self-end">
        <Button className="h-8 text-[12.5px]" onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          className="h-8 text-[12.5px]"
          disabled={name.trim().length === 0 || text.trim().length === 0}
          onClick={() => void onSave(name.trim(), text.trim())}
        >
          {initial ? 'Save phrase' : 'Add phrase'}
        </Button>
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// After they accept
// ---------------------------------------------------------------------------

/**
 * Up to three short lines the thank-you shows as "What happens next" (§10.4).
 *
 * Empty by default, and then the thank-you says only that the company has the
 * signed proposal — which is true and short, where an invented "we will call
 * you within two days" is a promise nobody made.
 */
function NextSteps({ draft, canEdit, onChange }: {
  draft: ProposalDefaults
  canEdit: boolean
  onChange: (steps: string[]) => void
}) {
  /*
   * Three boxes locally, a list of the non-empty ones outward.
   *
   * The stored value is "the lines the thank-you prints", so a blank middle
   * box must not become a blank printed line — but while somebody is typing,
   * box 2 being empty with box 3 filled is a perfectly ordinary half-finished
   * state, and compacting the array under the cursor moves what they are
   * typing into a different box. So the boxes keep their positions and only
   * what leaves compacts.
   */
  const [boxes, setBoxes] = useState<string[]>(
    () => [0, 1, 2].map((index) => draft.nextSteps[index] ?? ''))

  return (
    <SettingsCard title="After they accept" sub="shown on the thank-you page">
      {boxes.map((value, index) => (
        <div key={index} className="border-b border-line-faint px-[18px] py-2.5 last:border-b-0">
          <TextInput
            value={value}
            disabled={!canEdit}
            maxLength={120}
            placeholder={index === 0 ? 'We will call to book the dates.' : 'Optional'}
            onChange={(event) => {
              const next = boxes.map((one, at) => (at === index ? event.target.value : one))
              setBoxes(next)
              onChange(next.map((one) => one.trim()).filter((one) => one !== ''))
            }}
          />
        </div>
      ))}
      <p className="m-0 px-[18px] py-2.5 text-[12px] leading-[1.45] text-text-muted">
        Left empty, the thank-you says only that you have the signed proposal — which is
        true and short, where an invented promise is one nobody made.
      </p>
    </SettingsCard>
  )
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/**
 * One default (board 15): the label and its sentence in a 300px column, the
 * control **beside it on the left** rather than pushed to the card's far edge —
 * a segmented control 400px from the words it answers reads as a different
 * row. Under 900px the two stack.
 */
function Row({ label, help, children }: {
  label: string
  help: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-5 border-b border-line-faint px-[18px] py-[13px]
                    last:border-b-0 max-[900px]:flex-col max-[900px]:items-start
                    max-[900px]:gap-2">
      <div className="flex w-[300px] shrink-0 flex-col gap-0.5 max-[900px]:w-full">
        <b className="text-[13px] font-bold text-text">{label}</b>
        <span className="text-[12px] leading-[1.4] text-text-muted">{help}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

/** One of the three places, board 15's way: a dark numeral and one sentence. */
function Hand({ number, title, body }: { number: string; title: string; body: string }) {
  return (
    <div className="flex gap-2.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-text
                       text-[11px] font-bold text-surface">
        {number}
      </span>
      <span><b className="font-bold">{title}</b> {body}</span>
    </div>
  )
}

/**
 * The default terms (board 15), read-only: the words are edited in
 * Catalog › Terms, and a second editor here would be two places to change
 * what customers sign.
 */
function TermsCard({ terms }: { terms: ProposalDefaultTerms | null }) {
  return (
    <SettingsCard title="Terms of service" sub="the agreement every proposal carries">
      <div className="flex items-center gap-3 px-[18px] py-3.5">
        <span className={cn('inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center',
          'rounded-tile', terms ? 'bg-accent-soft text-accent' : 'bg-warn-bg text-warn')}>
          <IconFile size={17} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <b className="truncate text-[13px] font-bold text-text">
            {terms ? `${terms.name} · version ${terms.revision}` : 'No default terms'}
          </b>
          <span className="text-[12px] text-text-muted">
            {terms
              ? [
                terms.sections === 1 ? '1 section' : `${terms.sections} sections`,
                terms.opensFirst ? `opens with ${terms.opensFirst} showing` : '',
                'edited in Catalog › Terms',
              ].filter(Boolean).join(' · ')
              : 'New quotes carry no terms until one set is marked the default.'}
          </span>
        </div>
        <a
          href="/settings/catalog?tab=terms"
          className="inline-flex h-[30px] shrink-0 items-center rounded-button border border-line
                     bg-surface px-2.5 text-[12.5px] font-semibold text-text no-underline
                     hover:border-line-strong"
        >
          Edit terms
        </a>
      </div>
    </SettingsCard>
  )
}

const KIND_WORDS: Record<ProposalKind, string> = {
  material: 'Materials',
  labor: 'Labor',
  equipment: 'Equipment',
  fee: 'Fees',
}
